import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash, ArrowRight, Table as TableIcon } from "@phosphor-icons/react";

const SECTIONS = ["Gestione Dipendenti", "Gestione Sicurezza", "Gestione Pesi", "Amministrazione"];

export default function Tables() {
  const [tables, setTables] = useState([]);
  const [draft, setDraft] = useState({});
  const nav = useNavigate();

  const load = useCallback(async () => {
    try { setTables((await api.get("/tables")).data); }
    catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (key) => {
    const value = (draft[key] || "").trim();
    if (!value) return;
    try {
      await api.post(`/tables/${key}`, { value });
      toast.success("Valore aggiunto");
      setDraft({ ...draft, [key]: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (key, value) => {
    try { await api.delete(`/tables/${key}/${encodeURIComponent(value)}`); toast.success("Valore rimosso"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="tables-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Tabelle</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Tutte le anagrafiche di supporto collegate ai moduli del gestionale: modifica qui i valori e li
          ritrovi nei menu a tendina di presenze, sicurezza e pesi.
        </p>
      </div>

      {SECTIONS.map((section) => {
        const list = tables.filter((t) => t.section === section);
        if (!list.length) return null;
        return (
          <div key={section} className="space-y-3">
            <div className="flex items-center gap-2">
              <TableIcon size={16} className="text-primary" />
              <h2 className="font-head font-semibold text-base md:text-lg">{section}</h2>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {list.map((t) => (
                <div key={t.key} className="rounded-sm border border-border bg-card p-4 flex flex-col"
                  data-testid={`table-card-${t.key}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-head font-bold truncate">{t.label}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{t.hint}</div>
                    </div>
                    <span className="shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-sm bg-secondary tabular-nums"
                      data-testid={`table-count-${t.key}`}>{t.count}</span>
                  </div>

                  {t.editable ? (
                    <>
                      <div className="mt-3 flex flex-wrap gap-1.5 max-h-40 overflow-y-auto scroll-thin">
                        {t.items.map((v) => (
                          <span key={v} data-testid={`table-item-${t.key}-${v}`}
                            className="group inline-flex items-center gap-1 rounded-sm border border-border bg-secondary/60 px-2 py-1 text-xs">
                            {v}
                            <button className="opacity-40 group-hover:opacity-100 hover:text-rose-500 transition-opacity"
                              data-testid={`table-del-${t.key}-${v}`} onClick={() => del(t.key, v)}>
                              <Trash size={12} />
                            </button>
                          </span>
                        ))}
                        {t.items.length === 0 && <span className="text-xs text-muted-foreground">Nessun valore</span>}
                      </div>
                      <div className="mt-3 flex gap-2">
                        <Input value={draft[t.key] || ""} placeholder="Nuovo valore"
                          onChange={(e) => setDraft({ ...draft, [t.key]: e.target.value })}
                          onKeyDown={(e) => e.key === "Enter" && add(t.key)}
                          className="h-8 rounded-sm" data-testid={`table-input-${t.key}`} />
                        <Button size="sm" className="h-8 rounded-sm" data-testid={`table-add-${t.key}`}
                          onClick={() => add(t.key)}><Plus size={14} /></Button>
                      </div>
                    </>
                  ) : (
                    <Button variant="outline" size="sm" className="mt-4 rounded-sm w-full justify-between"
                      data-testid={`table-open-${t.key}`} onClick={() => nav(t.link)}>
                      Apri tabella <ArrowRight size={14} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
